import { useState, useContext } from "react";
import { toast } from "react-toastify";
import { GlobalContext } from "../../Context/GlobalContext";

export const usePosOrderSettings = () => {
  const { api, userID } = useContext(GlobalContext);

  const [loading, setLoading] = useState(false);
  const [discountsList, setDiscountsList] = useState([]);
  const [printSettingDetails, setPrintSettingDetails] = useState(null);
  const [deliveryCharges, setDeliveryCharges] = useState([]);
  const [customizations, setCustomizations] = useState([]);
  const [drawerSessions, setDrawerSessions] = useState([]);
  const [drawerLedger, setDrawerLedger] = useState([]);
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  const [ledgerLoading, setLedgerLoading] = useState(false);

  // COMMON API HANDLER
  const handleApi = async (url, body, successMsg) => {
    setLoading(true);

    try {
      const res = await api.post(url, body);

      if (res?.data?.responseCode === "00") {
        if (successMsg) toast.success(successMsg);
        return res.data;
      } else {
        toast.error(
          res?.data?.responseMessage || "Something failed",
        );
        return null;
      }
    } catch (err) {
      toast.error("Something went wrong");
      return null;
    } finally {
      setLoading(false);
    }
  };

  // ---------------- DISCOUNT ----------------

  const getDiscounts = async () => {
    const res = await handleApi(
      "/Order/v2.0/getPosDiscountList",
      { userid: userID },
    );

    if (res) {
      setDiscountsList(res.response || []);
    }
  };

  const updatePosDiscount = async ({ orderType, discount, chargeType }) => {
  const res = await handleApi(
    "/Order/v1.0/updatePosDiscount",
    {
      userid: userID,
      orderType,
      discount,
      chargeType,
    },
    "Discount updated successfully",
  );

  if (res) {
    getDiscounts();
  }

  return res;
};

  const deleteDiscount = async (discountId) => {
    const res = await handleApi(
      "/Order/v1.0/deletePosDiscount",
      {
        userid: userID,
        discountId,
      },
      "Discount removed",
    );

    if (res) getDiscounts();
  };

  const getPosDeliveryCharges = async () => {
    setLoading(true);

    try {
      const res = await api.post(
        `/Order/getPosPackkyDeliveryCharges?fkuserid=${userID}`,
        {},
      );

      if (res?.data?.responseCode === "00") {
        setDeliveryCharges(
          res?.data?.response || [],
        );

        return res.data;
      } else {
        toast.error(
          res?.data?.responseMessage ||
            "Failed to fetch delivery charges",
        );

        return null;
      }
    } catch (err) {
      toast.error("Something went wrong");
      return null;
    } finally {
      setLoading(false);
    }
  };

  const addCustomization = async (payload) => {
    const res = await handleApi(
      "/Order/v1.0/Pos/addCustomization",
      {
        userid: userID,
        ...payload,
      },
      "Customization added successfully",
    );

    return res;
  };

  const addPaymentMethod = async (method) => {
    if (!method?.trim()) {
      toast.warning("Please enter a payment method");
      return null;
    }

    const res = await handleApi(
      "/Order/v1.0/Pos/addPaymentMethodOption",
      {
        userid: userID,
        paymentMethod: method.trim(),
      },
      "Payment method added successfully",
    );

    return res;
  };

  const deleteCustomization = async (id) => {
    const res = await handleApi(
      "/Order/v1.0/Pos/deleteCustomization",
      {
        userid: userID,
        customizationId: id,
      },
      "Customization deleted successfully",
    );

    if (res) {
      getCustomizations();
    }

    return res;
  };

  const deletePaymentMethod = async (id) => {
    const res = await handleApi(
      "/Order/v1.0/Pos/deletePaymentMethodOption",
      {
        userid: userID,
        paymentMethodId: id,
      },
      "Payment method deleted successfully",
    );

    return res;
  };

  const updatePrintSettings = async (payload) => {
    const res = await handleApi(
      "/Order/v1.0/updatePrintSettings",
      payload,
      "Print settings updated",
    );

    return res;
  };

  const getCustomizations = async () => {
    const res = await handleApi(
      "/Order/v1.0/getPosOrderCustomizations",
      {
        userid: userID,
      },
    );

    if (res) {
      setCustomizations(res.response || []);
    }

    return res;
  };

  // ---------------- POS PAYMENTS / DRAWER SESSIONS ----------------

  const getDrawerAllSessions = async (payload) => {
    setPaymentsLoading(true);

    try {
      const res = await api.post(
        "/Order/getDrawerAllSessions",
        payload,
      );

      if (res?.data?.responseCode === "00") {
        setDrawerSessions(
          res?.data?.response || [],
        );

        return res.data;
      } else {
        setDrawerSessions([]);

        toast.error(
          res?.data?.responseMessage ||
            "Failed to fetch drawer sessions",
        );

        return null;
      }
    } catch (err) {
      console.error(
        "getDrawerAllSessions error:",
        err,
      );

      toast.error(
        "Something went wrong while fetching drawer sessions",
      );

      setDrawerSessions([]);
      return null;
    } finally {
      setPaymentsLoading(false);
    }
  };

  const getDrawerLedgerBySessionId = async (payload) => {
    setLedgerLoading(true);

    try {
      const res = await api.post(
        "/Order/getDrawerLedgerBySessionId",
        payload,
      );

      if (res?.data?.responseCode === "00") {
        setDrawerLedger(
          res?.data?.response || [],
        );

        return res.data;
      } else {
        setDrawerLedger([]);

        toast.error(
          res?.data?.responseMessage ||
            "Failed to fetch drawer ledger",
        );

        return null;
      }
    } catch (err) {
      console.error(
        "getDrawerLedgerBySessionId error:",
        err,
      );

      toast.error(
        "Something went wrong while fetching ledger",
      );

      setDrawerLedger([]);
      return null;
    } finally {
      setLedgerLoading(false);
    }
  };

  const clearDrawerLedger = () => {
    setDrawerLedger([]);
  };

  const clearDrawerSessions = () => {
    setDrawerSessions([]);
    setDrawerLedger([]);
  };

  return {
    loading,

    // state
    discountsList,
    printSettingDetails,

    // discount
    getDiscounts,
    updatePosDiscount,
    deleteDiscount,

    // print
    updatePrintSettings,

    deliveryCharges,
    getPosDeliveryCharges,

    customizations,
    getCustomizations,
    addCustomization,
    deleteCustomization,

    addPaymentMethod,
    deletePaymentMethod,

    // drawer sessions
    drawerSessions,
    getDrawerAllSessions,
    clearDrawerSessions,

    // drawer ledger
    clearDrawerLedger,
    getDrawerLedgerBySessionId,
    drawerLedger,
    paymentsLoading,
    ledgerLoading,
  };
};
